import React, { useRef, useState, useCallback, useEffect } from 'react';
import './GraffitiOverlay.css';

const TOOLS = [
    { id: 'pen', label: 'Pen', icon: 'M' },
    { id: 'line', label: 'Line', icon: '/' },
    { id: 'circle', label: 'Circle', icon: 'O' },
    { id: 'arrow', label: 'Arrow', icon: '\u2192' },
    { id: 'text', label: 'Text', icon: 'T' },
];

const COLORS = [
    { id: 'red', value: '#ef4444' },
    { id: 'yellow', value: '#fbbf24' },
    { id: 'white', value: '#ffffff' },
    { id: 'green', value: '#22c55e' },
    { id: 'cyan', value: '#06b6d4' },
];

function drawCommandOnCanvas(ctx, cmd) {
    switch (cmd.type) {
        case 'pen': {
            if (!cmd.points || cmd.points.length < 2) return;
            ctx.strokeStyle = cmd.color;
            ctx.lineWidth = cmd.thickness;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(cmd.points[0].x, cmd.points[0].y);
            for (let i = 1; i < cmd.points.length; i++) {
                ctx.lineTo(cmd.points[i].x, cmd.points[i].y);
            }
            ctx.stroke();
            break;
        }
        case 'line': {
            ctx.strokeStyle = cmd.color;
            ctx.lineWidth = cmd.thickness;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(cmd.x1, cmd.y1);
            ctx.lineTo(cmd.x2, cmd.y2);
            ctx.stroke();
            break;
        }
        case 'circle': {
            ctx.strokeStyle = cmd.color;
            ctx.lineWidth = cmd.thickness;
            ctx.beginPath();
            ctx.arc(cmd.cx, cmd.cy, cmd.radius, 0, Math.PI * 2);
            ctx.stroke();
            break;
        }
        case 'arrow': {
            ctx.strokeStyle = cmd.color;
            ctx.lineWidth = cmd.thickness;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(cmd.x1, cmd.y1);
            ctx.lineTo(cmd.x2, cmd.y2);
            ctx.stroke();
            const angle = Math.atan2(cmd.y2 - cmd.y1, cmd.x2 - cmd.x1);
            const headLen = Math.max(cmd.thickness * 4, 12);
            ctx.fillStyle = cmd.color;
            ctx.beginPath();
            ctx.moveTo(cmd.x2, cmd.y2);
            ctx.lineTo(cmd.x2 - headLen * Math.cos(angle - Math.PI / 6), cmd.y2 - headLen * Math.sin(angle - Math.PI / 6));
            ctx.lineTo(cmd.x2 - headLen * Math.cos(angle + Math.PI / 6), cmd.y2 - headLen * Math.sin(angle + Math.PI / 6));
            ctx.closePath();
            ctx.fill();
            break;
        }
        case 'text': {
            ctx.font = `bold ${cmd.fontSize}px sans-serif`;
            ctx.fillStyle = cmd.color;
            ctx.textBaseline = 'top';
            ctx.fillText(cmd.text, cmd.x, cmd.y);
            break;
        }
    }
}

/**
 * useGraffiti - Manages all graffiti drawing state and handlers.
 */
export function useGraffiti(videoElement, graffitiFeature, isActive, nativeResolution) {
    const canvasRef = useRef(null);
    const textInputRef = useRef(null);
    const [tool, setTool] = useState('pen');
    const [color, setColor] = useState('#ef4444');
    const [thickness, setThickness] = useState(4);
    const [fontSize, setFontSize] = useState(32);
    const [commandCount, setCommandCount] = useState(0);
    const [textInput, setTextInput] = useState(null);

    const isDrawingRef = useRef(false);
    const drawStartRef = useRef(null);
    const penPointsRef = useRef([]);
    const localCommandsRef = useRef([]);
    const overlayShownRef = useRef(false);
    const toolRef = useRef(tool);
    const colorRef = useRef(color);
    const thicknessRef = useRef(thickness);
    const fontSizeRef = useRef(fontSize);

    toolRef.current = tool;
    colorRef.current = color;
    thicknessRef.current = thickness;
    fontSizeRef.current = fontSize;

    const redrawLocal = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (const cmd of localCommandsRef.current) {
            drawCommandOnCanvas(ctx, cmd);
        }
    }, []);

    // Resize canvas to match video rendered area
    // isActive is in deps so the effect re-runs when graffiti is toggled on
    // (canvasRef is null until GraffitiCanvas mounts, which requires isActive=true)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !videoElement) return;

        const sync = () => {
            const videoRect = videoElement.getBoundingClientRect();
            const overlayDiv = canvas.parentElement;
            if (!overlayDiv) return;
            const overlayRect = overlayDiv.getBoundingClientRect();

            // Calculate the video's actual rendered area (object-fit: contain)
            const videoW = videoElement.videoWidth || 1920;
            const videoH = videoElement.videoHeight || 1080;
            const videoAspect = videoW / videoH;
            const containerW = videoRect.width;
            const containerH = videoRect.height;

            let renderW, renderH, offsetX, offsetY;
            if (containerW / containerH > videoAspect) {
                // Letterboxed on sides
                renderH = containerH;
                renderW = renderH * videoAspect;
                offsetX = (containerW - renderW) / 2;
                offsetY = 0;
            } else {
                // Letterboxed on top/bottom
                renderW = containerW;
                renderH = renderW / videoAspect;
                offsetX = 0;
                offsetY = (containerH - renderH) / 2;
            }

            // Position canvas over the rendered video area within the overlay
            const left = videoRect.left - overlayRect.left + offsetX;
            const top = videoRect.top - overlayRect.top + offsetY;
            canvas.style.left = `${left}px`;
            canvas.style.top = `${top}px`;
            canvas.style.width = `${renderW}px`;
            canvas.style.height = `${renderH}px`;

            const newW = Math.round(renderW);
            const newH = Math.round(renderH);
            if (canvas.width !== newW || canvas.height !== newH) {
                canvas.width = newW;
                canvas.height = newH;
                redrawLocal();
            }
        };

        sync();
        const interval = setInterval(sync, 500);
        const observer = new ResizeObserver(sync);
        observer.observe(videoElement);
        return () => { clearInterval(interval); observer.disconnect(); };
    }, [videoElement, redrawLocal, isActive]);

    // Show/hide desktop overlay
    useEffect(() => {
        if (!graffitiFeature) return;
        if (isActive && !overlayShownRef.current) {
            graffitiFeature.show();
            overlayShownRef.current = true;
        } else if (!isActive && overlayShownRef.current && localCommandsRef.current.length === 0) {
            graffitiFeature.hide();
            overlayShownRef.current = false;
        }
    }, [isActive, graffitiFeature]);

    // Convert canvas coordinates to normalized (0-1) values.
    // The desktop overlay will scale these to its own window dimensions.
    const toNormalized = useCallback((canvasX, canvasY) => {
        const canvas = canvasRef.current;
        if (!canvas || !canvas.width) return { x: 0, y: 0 };
        return {
            x: canvasX / canvas.width,
            y: canvasY / canvas.height
        };
    }, []);

    const getCanvasCoords = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvas.width / rect.width),
            y: (e.clientY - rect.top) * (canvas.height / rect.height)
        };
    }, []);

    const buildShape = useCallback((start, end, t, c, th) => {
        switch (t) {
            case 'line':
                return { type: 'line', x1: start.x, y1: start.y, x2: end.x, y2: end.y, color: c, thickness: th };
            case 'circle': {
                const dx = end.x - start.x, dy = end.y - start.y;
                return { type: 'circle', cx: start.x, cy: start.y, radius: Math.sqrt(dx * dx + dy * dy), color: c, thickness: th };
            }
            case 'arrow':
                return { type: 'arrow', x1: start.x, y1: start.y, x2: end.x, y2: end.y, color: c, thickness: th };
            default: return null;
        }
    }, []);

    const handleMouseDown = useCallback((e) => {
        if (!isActive) return;
        e.preventDefault();
        e.stopPropagation();
        const coords = getCanvasCoords(e);
        if (!coords) return;

        if (toolRef.current === 'text') {
            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            setTextInput({
                canvasX: coords.x, canvasY: coords.y,
                screenX: e.clientX - rect.left, screenY: e.clientY - rect.top
            });
            setTimeout(() => textInputRef.current?.focus(), 0);
            return;
        }

        isDrawingRef.current = true;
        drawStartRef.current = coords;
        if (toolRef.current === 'pen') penPointsRef.current = [coords];
    }, [isActive, getCanvasCoords]);

    const handleMouseMove = useCallback((e) => {
        if (!isDrawingRef.current || !drawStartRef.current || !isActive) return;
        e.preventDefault();
        e.stopPropagation();
        const coords = getCanvasCoords(e);
        if (!coords) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const t = toolRef.current, c = colorRef.current, th = thicknessRef.current;

        if (t === 'pen') {
            penPointsRef.current.push(coords);
            const pts = penPointsRef.current;
            if (pts.length >= 2) {
                ctx.strokeStyle = c;
                ctx.lineWidth = th;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.beginPath();
                ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
                ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
                ctx.stroke();
            }
        } else {
            redrawLocal();
            const preview = buildShape(drawStartRef.current, coords, t, c, th);
            if (preview) drawCommandOnCanvas(ctx, preview);
        }
    }, [isActive, getCanvasCoords, redrawLocal, buildShape]);

    const handleMouseUp = useCallback((e) => {
        if (!isDrawingRef.current || !isActive) return;
        e.preventDefault();
        e.stopPropagation();
        const coords = getCanvasCoords(e);
        if (!coords) return;

        isDrawingRef.current = false;
        const t = toolRef.current, c = colorRef.current, th = thicknessRef.current;
        const start = drawStartRef.current;

        // Ensure desktop overlay is shown
        if (!overlayShownRef.current && graffitiFeature) {
            graffitiFeature.show();
            overlayShownRef.current = true;
        }

        if (t === 'pen') {
            const pts = penPointsRef.current;
            if (pts.length >= 2) {
                localCommandsRef.current.push({ type: 'pen', points: [...pts], color: c, thickness: th });
                setCommandCount(localCommandsRef.current.length);
                if (graffitiFeature) {
                    const nPts = pts.map(p => toNormalized(p.x, p.y));
                    graffitiFeature.drawLine(nPts, c, th);
                }
            }
            penPointsRef.current = [];
        } else if (start) {
            const localCmd = buildShape(start, coords, t, c, th);
            if (localCmd) {
                localCommandsRef.current.push(localCmd);
                setCommandCount(localCommandsRef.current.length);
                redrawLocal();

                if (graffitiFeature) {
                    const nStart = toNormalized(start.x, start.y);
                    const nEnd = toNormalized(coords.x, coords.y);
                    switch (t) {
                        case 'line':
                            graffitiFeature.drawStraightLine(nStart.x, nStart.y, nEnd.x, nEnd.y, c, th);
                            break;
                        case 'circle': {
                            const dx = coords.x - start.x, dy = coords.y - start.y;
                            const r = Math.sqrt(dx * dx + dy * dy);
                            const canvas = canvasRef.current;
                            const nRadius = canvas ? r / canvas.width : 0;
                            graffitiFeature.drawCircle(nStart.x, nStart.y, nRadius, c, th);
                            break;
                        }
                        case 'arrow':
                            graffitiFeature.drawArrow(nStart.x, nStart.y, nEnd.x, nEnd.y, c, th);
                            break;
                    }
                }
            }
        }
        drawStartRef.current = null;
    }, [isActive, getCanvasCoords, toNormalized, redrawLocal, buildShape, graffitiFeature]);

    const handleMouseLeave = useCallback(() => {
        if (isDrawingRef.current) {
            isDrawingRef.current = false;
            drawStartRef.current = null;
            penPointsRef.current = [];
            redrawLocal();
        }
    }, [redrawLocal]);

    const handleClear = useCallback(() => {
        localCommandsRef.current = [];
        setCommandCount(0);
        redrawLocal();
        if (graffitiFeature) graffitiFeature.clearAll();
        if (overlayShownRef.current && graffitiFeature) {
            graffitiFeature.hide();
            overlayShownRef.current = false;
        }
    }, [redrawLocal, graffitiFeature]);

    const handleUndo = useCallback(() => {
        if (localCommandsRef.current.length > 0) {
            localCommandsRef.current.pop();
            setCommandCount(localCommandsRef.current.length);
            redrawLocal();
        }
        if (graffitiFeature) graffitiFeature.undo();
    }, [redrawLocal, graffitiFeature]);

    const commitTextInput = useCallback((text) => {
        if (!text || !textInput) { setTextInput(null); return; }
        const { canvasX, canvasY } = textInput;
        const nCoords = toNormalized(canvasX, canvasY);
        const canvas = canvasRef.current;
        const fs = fontSizeRef.current;
        const c = colorRef.current;
        const scaledFontSize = fs * (canvas?.width || 1920) / 1920;
        localCommandsRef.current.push({ type: 'text', x: canvasX, y: canvasY, text, color: c, fontSize: scaledFontSize });
        setCommandCount(localCommandsRef.current.length);
        redrawLocal();
        if (!overlayShownRef.current && graffitiFeature) {
            graffitiFeature.show();
            overlayShownRef.current = true;
        }
        if (graffitiFeature) graffitiFeature.drawText(nCoords.x, nCoords.y, text, c, fs);
        setTextInput(null);
    }, [textInput, toNormalized, redrawLocal, graffitiFeature]);

    return {
        canvasRef, textInputRef,
        tool, setTool, color, setColor, thickness, setThickness, fontSize, setFontSize,
        commandCount, textInput, setTextInput,
        handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave,
        handleClear, handleUndo, commitTextInput,
        hasDrawings: commandCount > 0,
    };
}

/**
 * GraffitiCanvas - Canvas overlay inside the video container.
 * Drawings persist regardless of mode. Only "Clear All" removes them.
 */
export const GraffitiCanvas = ({ graffiti, isActive }) => {
    const {
        canvasRef, textInputRef,
        tool, color, fontSize,
        textInput, setTextInput, commitTextInput,
        handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave,
        hasDrawings,
    } = graffiti;

    if (!isActive && !hasDrawings) return null;

    return (
        <div className={`graffiti-overlay ${isActive ? 'active' : 'display-only'}`}>
            <canvas
                ref={canvasRef}
                className="graffiti-overlay-canvas"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            />
            {textInput && isActive && (
                <input
                    ref={textInputRef}
                    className="graffiti-overlay-text-input"
                    style={{
                        left: textInput.screenX,
                        top: textInput.screenY,
                        color,
                        fontSize: `${fontSize}px`,
                    }}
                    placeholder="Type here..."
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') commitTextInput(e.target.value);
                        if (e.key === 'Escape') setTextInput(null);
                    }}
                    onBlur={(e) => commitTextInput(e.target.value)}
                />
            )}
            {isActive && (
                <div className="graffiti-overlay-hint">
                    {tool === 'pen' ? 'Click and drag to draw' :
                     tool === 'text' ? 'Click to place text' :
                     tool === 'circle' ? 'Click center, drag radius' :
                     'Click start, release end'}
                </div>
            )}
        </div>
    );
};

/**
 * GraffitiToolbar - Tool selection bar rendered below the video.
 */
export const GraffitiToolbar = ({ graffiti }) => {
    const {
        tool, setTool, color, setColor, thickness, setThickness, fontSize, setFontSize,
        commandCount, handleClear, handleUndo,
    } = graffiti;

    return (
        <div className="graffiti-toolbar">
            <div className="graffiti-toolbar-group">
                <label>Tool</label>
                {TOOLS.map(t => (
                    <button
                        key={t.id}
                        className={`graffiti-tool-btn ${tool === t.id ? 'active' : ''}`}
                        onClick={() => setTool(t.id)}
                        data-tooltip={t.label}
                    >
                        {t.icon}
                    </button>
                ))}
            </div>
            <div className="graffiti-toolbar-divider" />
            <div className="graffiti-toolbar-group">
                <label>Color</label>
                {COLORS.map(c => (
                    <button
                        key={c.id}
                        className={`graffiti-color-swatch ${color === c.value ? 'active' : ''}`}
                        style={{ backgroundColor: c.value }}
                        onClick={() => setColor(c.value)}
                        data-tooltip={c.id}
                    />
                ))}
            </div>
            <div className="graffiti-toolbar-divider" />
            <div className="graffiti-toolbar-group">
                <label>Size</label>
                {tool === 'text' ? (
                    <>
                        <input type="range" className="graffiti-slider" min="16" max="72"
                            value={fontSize} onChange={(e) => setFontSize(Number(e.target.value))} />
                        <span className="graffiti-slider-value">{fontSize}px</span>
                    </>
                ) : (
                    <>
                        <input type="range" className="graffiti-slider" min="2" max="12"
                            value={thickness} onChange={(e) => setThickness(Number(e.target.value))} />
                        <span className="graffiti-slider-value">{thickness}px</span>
                    </>
                )}
            </div>
            <div className="graffiti-toolbar-divider" />
            <div className="graffiti-toolbar-group">
                <button className="graffiti-action-btn" onClick={handleUndo} disabled={commandCount === 0}>Undo</button>
                <button className="graffiti-action-btn danger" onClick={handleClear} disabled={commandCount === 0}>Clear All</button>
            </div>
            {commandCount > 0 && (
                <>
                    <div className="graffiti-toolbar-divider" />
                    <span className="graffiti-command-count">{commandCount} drawn</span>
                </>
            )}
        </div>
    );
};
